import React, { useState, useEffect, useCallback } from 'react';
import Image from 'src/components/Common/ImageFluid';
import PortableText from '@sanity/block-content-to-react';
import Icon from 'src/components/Common/Icon';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { encode } from 'shopify-gid';
import { eventData } from 'src/utils/dataLayer';
import _ from 'lodash';
import {
  Card,
  CardImageContainer,
  CardDetail,
  CaseTitle,
  CasePrice,
  Quantity,
  QuantityButton,
  NumberInput,
  IconWrapper,
  CaseDescription
} from './styles';

import {
  useAddItemToCart,
  useUpdateItemsFromCart,
  useRemoveItemFromCart,
} from 'src/context/siteContext';

async function shopifyFetch({ query, variables }: { query: string; variables?: any }) {
  const endpoint = `https://${process.env.GATSBY_SHOPIFY_STORE}/api/2023-10/graphql.json`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN || "",
    },
    body: JSON.stringify({ query, variables }),
  });
  return response.json();
}

const PRODUCT_VARIANT_QUERY = `
  query getProduct($id: ID!) {
    product(id: $id) {
      id
      title
      variants(first: 100) {
        edges {
          node {
            id
            title
            availableForSale
            sku
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

interface CaseCardProps {
  pillow?: any;
  canAdd?: boolean;
  lineItems?: any;
}

const CaseCard = ({ pillow, canAdd, lineItems }: CaseCardProps) => {
  const { addItemToCart } = useAddItemToCart();
  const removeFromCart = useRemoveItemFromCart();
  const updateItemsFromCart = useUpdateItemsFromCart();
  // const lineItems = useCartItems();
  const breakpoints = useBreakpoint();
  const [isOpen, setIsOpen] = useState(false);
  const [pillowcaseQuantity, setPillowcaseQuantity] = useState<number>(0);
  const productId = pillow?.content?.shopify?.productId;
  const [variant, setVariant] = useState<any | undefined>();
  const [currentItem, setCurrentItem] = useState<any | undefined>();
  const [productTitle, setProductTitle] = useState("" as string);

  useEffect(() => {
    const shopifyId = encode('Product', productId, {
      accessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
    });
    
    const fetchProduct = async () => {
      try {
        const { data } = await shopifyFetch({
          query: PRODUCT_VARIANT_QUERY,
          variables: { id: shopifyId }
        });

        const product = data?.product;
        if (!product) return;

        setProductTitle(product.title);
        const decodedVariants = product.variants.edges.map(({ node }: any) => {
          return {
            ...node,
            available: node.availableForSale,
            priceV2: { amount: node.price.amount },
            price: node.price.amount, 
          };
        });

        const availableVariant = decodedVariants?.find((variant: any) => variant);
        if (availableVariant?.available) {
          setVariant(availableVariant);
        }
      } catch (error) {
        console.error("Error fetching product in CaseCard", error);
      }
    };

    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (variant && lineItems.length) {
      const findCurrent = lineItems.find(
        (item: any) => item?.variant?.id === variant?.id
      );
      setCurrentItem(findCurrent);
    }
  }, [lineItems, variant]);

  useEffect(() => {
    if (currentItem) {
      setPillowcaseQuantity(currentItem?.quantity);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [currentItem]);

  const debouncedChange = useCallback(
    _.debounce((qty, id) => {
      updateItemsFromCart({ id, quantity: qty });
    }, 150),
    []
  );

  const handlePillow = async () => {

    setPillowcaseQuantity(1);
    dataLayer?.push({
      ...eventData('add_to_cart'),
      ecommerce: {
        value: parseFloat(variant?.priceV2?.amount),
        currencyCode: process.env.GATSBY_CURRENCY_SYMBOL == '$' ? 'USD' : 'GBP',
        items: [
          {
            name: productTitle,
            id: variant?.sku,
            price: parseFloat(variant?.priceV2?.amount),
            brand: 'GroovePillows',
            variant: variant?.title,
            quantity: 1,
          },
        ],
      }
    });
    await addItemToCart(variant?.id, 1, [], false);
  };

  const addPillowcaseQuantity = () => {
    const selectedItemInCart = lineItems.find(
      (item: any) => item?.variant?.id === variant?.id
    );
    if(selectedItemInCart) {
      setPillowcaseQuantity(selectedItemInCart.quantity + 1);
      debouncedChange(selectedItemInCart.quantity + 1, selectedItemInCart.id);
    }
  };

  const removePillowcaseQuantity = () => {
    if (pillowcaseQuantity > 1) {
      setPillowcaseQuantity(pillowcaseQuantity - 1);
      const selectedItemInCart = lineItems.find(
        (item: any) => item?.variant?.id === variant?.id
      );
      if(selectedItemInCart) {
        setPillowcaseQuantity(selectedItemInCart.quantity - 1);
        debouncedChange(selectedItemInCart.quantity - 1, selectedItemInCart.id);
      }
    } else {
      debouncedChange.cancel();
      const selectedItemInCart = lineItems.find(
        (item: any) => item?.variant?.id === variant?.id
      );
      if(selectedItemInCart) {
        dataLayer.push({
          event: "remove_from_cart",
          ecommerce: {
            currencyCode: process.env.GATSBY_CURRENCY_SYMBOL == '$' ? 'USD' : 'GBP',
            items: [
              {
                item_id: variant?.sku,
                item_name: productTitle,
                currency: process.env.GATSBY_CURRENCY_SYMBOL == '$' ? 'USD' : 'GBP',
                item_brand: "GroovePillows",
                item_variant: variant?.title,
                price: parseFloat(variant?.price),
                quantity: 1
              }
            ]
          }
        });
        removeFromCart(selectedItemInCart?.id);
      }
      setIsOpen(false);
    }
  };

  return (
    <Card increaseHeight={pillow?.saveMoneyAddText}>
      <CardImageContainer>
        <Image
          imageRef={pillow?.content?.main?.caseImage}
          width={!breakpoints.tablet ? 47 : 56}
          height={!breakpoints.tablet ? 47 : 56}
        />
      </CardImageContainer>
      <CardDetail>
        <CaseTitle>
          <PortableText blocks={pillow?.content?.main?.caseTitle} /></CaseTitle>
        {pillow?.content?.main?.productDescription && (
          <CaseDescription>
          <PortableText blocks={pillow?.content?.main?.caseDescription} />
          </CaseDescription>
        )}
        <CasePrice>
          {process.env.GATSBY_CURRENCY_SYMBOL}
          {pillow?.content?.shopify?.defaultVariant?.price}
        </CasePrice>
      </CardDetail>
      {isOpen ? (
        <Quantity>
          <QuantityButton type="button" onClick={removePillowcaseQuantity}>
            <Icon name="minus" />
          </QuantityButton>
          <NumberInput
            type="number"
            name="quantity"
            readOnly={true}
            defaultValue={1}
            value={pillowcaseQuantity}
            min="1"
            max={pillow?.content?.shopify?.defaultVariant?.inventoryQuantity}
          />
          <QuantityButton type="button" onClick={addPillowcaseQuantity}>
            <Icon name="plus" />
          </QuantityButton>
        </Quantity>
      ) : (
        <IconWrapper
          onClick={() => {
            if (canAdd) {
              setIsOpen(true);
              handlePillow();
            }
          }}
        >
          <Icon name="pink-plus" />
        </IconWrapper>
      )}
    </Card>
  );
};

export default CaseCard;